'use client';

import * as React from 'react';

import { Icon } from '@/components/atoms/primitives/icon';
import { cn } from '@/components/atoms/primitives/utils';
import type { NotificationItem } from '@loci/api';
import {
  formatRelativeTime,
  hrefFromData,
  iconNameForType,
  isUnread,
} from '@/lib/notifications/groupNotifications';

export interface NotificationRowProps {
  readonly notification: NotificationItem;
  /** IANA timezone for relative-time / absolute-date fallback formatting. */
  readonly cityTz: string;
  /** Reference instant for relative-time formatting (injectable for tests). */
  readonly now?: Date;
  /**
   * Fired when the row is activated (click / Enter / Space). Receives the
   * row's `id` and its resolved deep-link `href` (or `null` when the
   * notification carries no navigable target). The parent owns navigation +
   * mark-read so the row stays presentational.
   */
  readonly onActivate: (id: string, href: string | null) => void;
  /** Roving-tabindex flag: only the active row is in the Tab order. */
  readonly isFocused: boolean;
  /** Index within the flattened row list — used for arrow-key navigation. */
  readonly rowIndex: number;
  /** Registers the row's DOM node so the parent can move focus across rows. */
  readonly registerRef: (rowIndex: number, node: HTMLButtonElement | null) => void;
}

/**
 * A single notification row (WEB-434). Presentational: renders the type icon,
 * title, body, relative time, and an unread dot. Activation is delegated to
 * the parent via `onActivate` so the panel can deep-link + mark-read in one
 * place. The row is a `<button>` participating in a roving-tabindex listbox so
 * arrow keys move focus and Enter/Space activate.
 */
export function NotificationRow({
  notification,
  cityTz,
  now,
  onActivate,
  isFocused,
  rowIndex,
  registerRef,
}: NotificationRowProps): React.ReactElement {
  const unread = isUnread(notification);
  const href = hrefFromData(notification.data);
  const relative = formatRelativeTime(notification.createdAt, cityTz, now);
  const iconName = iconNameForType(notification.type);

  const setRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      registerRef(rowIndex, node);
    },
    [registerRef, rowIndex],
  );

  const ariaLabel = [
    unread ? 'Unread notification' : 'Notification',
    notification.title,
    notification.body ?? '',
    relative,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <button
      ref={setRef}
      type="button"
      data-testid={`notification-row-${notification.id}`}
      data-unread={unread || undefined}
      aria-label={ariaLabel}
      tabIndex={isFocused ? 0 : -1}
      onClick={() => onActivate(notification.id, href)}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-3 text-left',
        'transition-colors outline-none',
        'hover:bg-muted/60 focus-visible:bg-muted/60',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-0',
        unread && 'bg-muted/30',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
          unread ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon name={iconName} className="size-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-foreground truncate text-sm font-medium">{notification.title}</span>
          {unread && (
            <span
              aria-hidden="true"
              data-testid={`notification-unread-dot-${notification.id}`}
              className="bg-primary size-2 shrink-0 rounded-full"
            />
          )}
        </span>
        {notification.body && (
          <span className="text-muted-foreground mt-0.5 line-clamp-2 block text-sm">
            {notification.body}
          </span>
        )}
      </span>

      <time
        dateTime={notification.createdAt}
        className="text-muted-foreground mt-0.5 shrink-0 text-xs tabular-nums"
      >
        {relative}
      </time>
    </button>
  );
}
